import { formatISO } from "date-fns";
import { normalizeTask } from "@/stores/helpers";
import { type ActorsObject } from "@/stores/planner.module";
import api from "@/api";
import type { PlannerTaskFragment } from "@/graphql";

type ActorIds = {
  employee: string[];
  department: string[];
  resident: string[];
};
export interface IGetPlannerTaskProps {
  dateFrom: string;
  dateTo: string;
}

export type BasePlannerTask = {
  id: string;
  title: string;
  description: string | null;
  completed: boolean;
  visibleForResident?: boolean;
  repeatId?: string;
  type: "SinglePlannerTask" | "RepeatingPlannerTask" | "task";
  approvalState?: string;
};

// Mapped to fit the already implemented normalization functions.
export type MappedQGLTask = BasePlannerTask & {
  taskImage?: string; // From picture?.source
  start: string | Date; // ISO date string
  end: string | Date; // ISO date string
  categoryId?: string;
  color?: string;
  categoryTitle?: string;
  actorIds: ActorIds;
  isRepeat: boolean;
  openSubtaskCount: number;
  actors?: ActorsObject; // Added during filtering
};

// The final datastructure that should reflect the structure used in the app.
export type PlannerTask = MappedQGLTask & {
  start: Date;
  end: Date;
  color: string;
  inverted: boolean;
  listKey: string;
  pastEnd: boolean;
  // entries?: Record<string, PlannerEntity>; // 2024-10-10: Task or Module Entity
};

export const taskMapper = (row: PlannerTaskFragment): PlannerTask => {
  type ExtendedMappedQGLTask = MappedQGLTask & {
    subtasks: any;
  };

  const task: ExtendedMappedQGLTask = {
    id: row.id,
    taskImage: row.picture?.source ?? row.pictogram?.source,
    approvalState: row.approvalState?.toLowerCase(),
    title: row.title,
    description: row.description || "",
    start: row.startDate,
    end: row.endDate,
    repeatId:
      row.__typename === "RepeatingPlannerTask" ? row.repeatId : undefined,
    categoryId: row.category?.id,
    color: row.category?.color,
    categoryTitle: row.category?.title,
    completed: row.completed,
    visibleForResident: row.visibleForResident || false,
    actorIds: {
      employee:
        row.assignees
          ?.filter((assignee) => assignee.type?.toLowerCase() === "employee")
          .map((assignee) => assignee.id) || [],
      department:
        row.assignees
          ?.filter((assignee) => assignee.type?.toLowerCase() === "department")
          .map((assignee) => assignee.id) || [],
      resident:
        row.assignees
          ?.filter((assignee) => assignee.type?.toLowerCase() === "resident")
          .map((assignee) => assignee.id) || [],
    },
    isRepeat: row.type === "RepeatingPlannerTask",
    openSubtaskCount: row.subtasks
      ? row.subtasks.filter((subtask) => !subtask.completed).length
      : 0,
    type: "task",
    subtasks: row.subtasks ?? [],
  };

  return normalizeTask(task);
};

const getItemsFromAPI = ({
  dateFrom,
  dateTo,
}: IGetPlannerTaskProps): Promise<PlannerTask[]> => {
  return new Promise((resolve, cancel) => {
    api.planner
      .getPlannerTasks(dateFrom, dateTo)
      .then((response) => {
        const PlannerTasks: PlannerTask[] = response.map(taskMapper);
        resolve(PlannerTasks);
      })
      .catch((error) => {
        // oxlint-disable-next-line no-console
        console.log("error", error);
        cancel(error);
      });
  });
};

export default async (
  variables: IGetPlannerTaskProps,
): Promise<PlannerTask[]> => {
  // Setting the parameters
  const start = new Date(variables.dateFrom);
  const end = new Date(variables.dateTo);

  return await getItemsFromAPI({
    dateFrom: formatISO(start),
    dateTo: formatISO(end),
  });
};
